/**
 * Role Management Page
 *
 * Admin interface for managing roles and permissions.
 *
 * Features:
 * - List all roles (system + custom)
 * - Create custom roles
 * - Edit custom roles (system roles cannot be modified)
 * - Delete custom roles
 * - View role permissions
 * - Assign permissions to roles
 *
 * SOC 2 Controls: CC6.2, CC6.7 - Role management with proper access controls
 */

import React, { useState, useEffect } from 'react';
import { formatRoleName } from '../../utils/roleUtils';
import { usePermissions } from '../../context/PermissionContext';
import { useAuth } from '../../context/AuthContext';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';

// const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

export default function RoleManagement({ user, onLogout, hideNavigation = false }) {
  const { showSuccess, showError, confirm } = useAlert();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionMode, setPermissionMode] = useState('view'); // 'view' (read-only) | 'edit'

  const { hasPermission } = usePermissions();
  const { token, user: authUser } = useAuth();

  // Check permissions - fallback to role check for PostgreSQL-authenticated admins
  const isAdmin = user?.role === 'Admin' || user?.role === 'admin' || authUser?.role === 'Admin' || authUser?.role === 'admin';
  const canCreateRoles = isAdmin || hasPermission('roles', 'create');
  const canUpdateRoles = isAdmin || hasPermission('roles', 'update');
  const canDeleteRoles = isAdmin || hasPermission('roles', 'delete');
  const canManagePermissions = isAdmin || hasPermission('permissions', 'manage');

  // Roles whose permissions a tenant Admin cannot edit — mirrors the backend
  // block in tenant_admin.update_role_permissions (corporate-name list OR any
  // non-site scope). For these we expose read-only "View Permissions" only.
  const CORPORATE_ROLE_NAMES = ['admin', 'super_admin', 'superadmin', 'platform_admin', 'hse_manager', 'director'];
  const isCorporateManaged = (role) => {
    const nm = (role?.name || '').toLowerCase();
    const sc = (role?.scope_type || '').toLowerCase();
    return CORPORATE_ROLE_NAMES.includes(nm) || (!!sc && sc !== 'site');
  };

  // Load roles and permissions
  useEffect(() => {
    if (token) {
      loadRoles();
      loadPermissions();
    }
  }, [token]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      // Use PostgreSQL tenant-admin endpoint
      const response = await fetch(`${API_BASE_URL}/tenant-admin/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load roles');

      const data = await response.json();
      setRoles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      // Use PostgreSQL tenant-admin endpoint
      const response = await fetch(`${API_BASE_URL}/tenant-admin/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load permissions');

      const data = await response.json();
      setPermissions(data);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    }
  };

  const handleDeleteRole = async (roleId) => {
    const confirmed = await confirm('Are you sure you want to delete this role? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/tenant-admin/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || 'Failed to delete role');
      }

      await loadRoles();
      showSuccess('Role deleted successfully');
    } catch (err) {
      showError(err.message);
    }
  };

  const handleOpenPermissions = (role, mode = 'view') => {
    setSelectedRole(role);
    setPermissionMode(mode);
    setShowPermissionModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  const content = (
    <>
      <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
            <p className="text-gray-600 mt-1">Manage roles and permissions for your organization</p>
          </div>
        {canCreateRoles && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Custom Role
          </button>
        )}
      </div>

      {/* Roles Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {/* BUG-272: Prefer display_name; fall back to humanised slug. */}
                    <div className="text-sm font-medium text-gray-900">{role.display_name || formatRoleName(role.name)}</div>
                    {role.is_system && (
                      <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        System
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{role.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    Level {role.level}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {role.is_system ? 'System Role' : 'Custom Role'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenPermissions(role, 'view')}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View Permissions
                    </button>
                    {canManagePermissions && !isCorporateManaged(role) && (
                      <button
                        onClick={() => handleOpenPermissions(role, 'edit')}
                        className="text-green-600 hover:text-green-900"
                      >
                        Edit Permissions
                      </button>
                    )}
                    {!role.is_system && canDeleteRoles && (
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadRoles();
            setShowCreateModal(false);
          }}
          token={token}
        />
      )}

      {/* Permission Modal */}
      {showPermissionModal && selectedRole && (
        <RolePermissionModal
          role={selectedRole}
          permissions={permissions}
          onClose={() => {
            setShowPermissionModal(false);
            setSelectedRole(null);
          }}
          onSuccess={() => {
            loadRoles();
          }}
          token={token}
          canManage={canManagePermissions && permissionMode === 'edit' && !isCorporateManaged(selectedRole)}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </>
  );

  // If embedded in another page (hideNavigation=true), return just the content
  if (hideNavigation) {
    return content;
  }

  // Otherwise, render with full page layout including navigation
  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </div>
    </div>
  );
}

/**
 * Create Role Modal Component
 */
function CreateRoleModal({ onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    level: 5,
    permission_ids: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/tenant-admin/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create role');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Create Custom Role</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Name
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Quality Auditor"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Describe the role's responsibilities..."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Level (1=highest, 10=lowest)
            </label>
            <input
              type="number"
              min="1"
              max="10"
              required
              value={formData.level}
              onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Users can only assign roles at their level or lower privilege
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Module hierarchy for organizing permissions in the modal.
 * Maps parent modules to their child submodules, each with associated resources.
 */
const MODULE_HIERARCHY = [
  {
    name: 'Safety',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    parentSlug: 'safety',
    children: [
      { name: 'Hazards', resources: ['hazards'], moduleSlug: 'hira' },
      { name: 'Risk Assessments', resources: ['risk_assessments', 'hira'], moduleSlug: 'hira' },
      { name: 'Permit to Work', resources: ['ptw'], moduleSlug: 'ptw' },
      { name: 'CAPA', resources: ['capa'], moduleSlug: 'capa' },
      { name: 'Incidents', resources: ['incidents'], moduleSlug: 'incidents' },
      { name: 'Audits', resources: ['audits'], moduleSlug: 'audits' },
      { name: 'Training', resources: ['training'], moduleSlug: 'training' },
      { name: 'Meetings', resources: ['meetings'], moduleSlug: 'meetings' },
      { name: 'Documents', resources: ['documents'], moduleSlug: 'documents' },
      { name: 'SSEM', resources: ['ssem'], moduleSlug: 'ssem' },
    ]
  },
  {
    name: 'Energy & Resources',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    parentSlug: 'energy',
    children: [
      { name: 'ERS Studies', resources: ['ers'], moduleSlug: 'ers' },
      { name: 'Energy Tracking', resources: ['energy'], moduleSlug: 'energy' },
      { name: 'Emissions Tracking', resources: ['emissions'], moduleSlug: 'emissions' },
    ]
  },
  {
    name: 'Sustainability',
    icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
    parentSlug: 'sustainability',
    children: [
      { name: 'CBAM', resources: ['cbam'], moduleSlug: 'cbam' },
      { name: 'GRI/CSRD', resources: ['gri'], moduleSlug: 'gri' },
      { name: 'BRSR', resources: ['brsr'], moduleSlug: 'brsr' },
      { name: 'LCA', resources: ['lca'], moduleSlug: 'lca' },
      { name: 'SGX', resources: ['sgx'], moduleSlug: 'sgx' },
      { name: 'EcoVadis', resources: ['ecovadis'], moduleSlug: 'ecovadis' },
      { name: 'SSuM', resources: ['ssum'], moduleSlug: 'ssum' },
    ]
  },
  {
    name: 'Board Governance',
    icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    parentSlug: 'board',
    children: [
      { name: 'ERM', resources: ['erm'], moduleSlug: 'erm' },
      { name: 'Board Process', resources: ['board_process'], moduleSlug: 'board_process' },
    ]
  },
  {
    name: 'System & Administration',
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    alwaysShow: true,
    children: [
      { name: 'Users', resources: ['users'] },
      { name: 'Roles', resources: ['roles'] },
      { name: 'Modules', resources: ['modules', 'module_settings'] },
      { name: 'Reports & Analytics', resources: ['reports', 'dashboards', 'analytics'] },
      { name: 'Settings & Audit Logs', resources: ['settings', 'audit_logs'] },
    ]
  },
];

/**
 * Get all resources covered by the hierarchy (to find unmatched "Other" permissions)
 */
const ALL_HIERARCHY_RESOURCES = new Set(
  MODULE_HIERARCHY.flatMap(parent =>
    parent.children.flatMap(child => child.resources)
  )
);

/**
 * Role Permission Modal Component - Module/Submodule Hierarchy
 *
 * Shows permissions grouped by module hierarchy with collapsible sections,
 * select-all toggles, and search filtering.
 *
 * SOC 2 CC6.2: Granular permission assignment at submodule level
 */
function RolePermissionModal({ role, permissions, onClose, onSuccess, token, canManage, showSuccess, showError }) {
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedParents, setExpandedParents] = useState({});
  const [expandedChildren, setExpandedChildren] = useState({});
  const { enabledModuleSlugs } = useModuleAccess();

  useEffect(() => {
    loadRolePermissions();
  }, [role.id]);

  // Expand all parents by default on load
  useEffect(() => {
    const expanded = {};
    MODULE_HIERARCHY.forEach(parent => { expanded[parent.name] = true; });
    setExpandedParents(expanded);
  }, []);

  const loadRolePermissions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant-admin/roles/${role.id}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedPermissions(data.map(p => p.id));
      }
    } catch (err) {
      console.error('Failed to load role permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant-admin/roles/${role.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permission_ids: selectedPermissions })
      });
      if (!response.ok) throw new Error('Failed to update permissions');
      onSuccess();
      showSuccess('Permissions updated successfully');
    } catch (err) {
      showError(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Get permissions for a list of resources, filtered by search
  const getPermsForResources = (resources) => {
    return permissions.filter(p => {
      const matchesResource = resources.includes(p.resource);
      if (!matchesResource) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(q) ||
        p.resource?.toLowerCase().includes(q) ||
        p.action?.toLowerCase().includes(q)
      );
    });
  };

  // Get "Other" permissions not in any hierarchy group
  const otherPermissions = permissions.filter(p => {
    if (ALL_HIERARCHY_RESOURCES.has(p.resource)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.resource?.toLowerCase().includes(q) ||
      p.action?.toLowerCase().includes(q)
    );
  });

  // Toggle all permissions in a set of IDs
  const toggleAll = (permIds) => {
    const allSelected = permIds.every(id => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !permIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...permIds])]);
    }
  };

  const toggleParent = (name) => {
    setExpandedParents(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleChild = (key) => {
    setExpandedChildren(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Count selected/total for a set of permissions
  const getSelectionCount = (perms) => {
    const selected = perms.filter(p => selectedPermissions.includes(p.id)).length;
    return { selected, total: perms.length };
  };

  // Format action name for display
  const formatAction = (action) => {
    return action.charAt(0).toUpperCase() + action.slice(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">{role.display_name || role.name} — {canManage ? 'Edit Permissions' : 'View Permissions'}</h2>
            <p className="text-sm text-gray-600">{role.description}</p>
            {!canManage && (
              <p className="text-xs text-gray-400 mt-0.5">Read-only view</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading permissions...</div>
        ) : (
          <>
            {/* Search bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div className="mb-2 text-sm text-gray-600">
              {canManage ? 'Select permissions for this role:' : 'Permissions assigned to this role:'}
              <span className="ml-2 font-medium">
                ({selectedPermissions.length} of {permissions.length} selected)
              </span>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {MODULE_HIERARCHY.map((parent) => {
                // Filter children to only show modules enabled for this tenant
                const visibleChildren = parent.alwaysShow
                  ? parent.children
                  : parent.children.filter(child => !child.moduleSlug || enabledModuleSlugs.includes(child.moduleSlug));

                // Hide entire parent if no children are enabled
                if (visibleChildren.length === 0) return null;

                // Collect all permissions under visible children
                const allParentPerms = visibleChildren.flatMap(child => getPermsForResources(child.resources));
                if (allParentPerms.length === 0 && searchQuery) return null;

                const parentCount = getSelectionCount(allParentPerms);
                const isExpanded = expandedParents[parent.name];

                return (
                  <div key={parent.name} className="border rounded-lg overflow-hidden">
                    {/* Parent header */}
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleParent(parent.name)}
                    >
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={parent.icon} />
                        </svg>
                        <span className="font-semibold text-gray-900">{parent.name}</span>
                        <span className="text-xs text-gray-500">
                          ({parentCount.selected}/{parentCount.total})
                        </span>
                      </div>
                      {canManage && allParentPerms.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAll(allParentPerms.map(p => p.id));
                          }}
                          className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          {parentCount.selected === parentCount.total ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>

                    {/* Children */}
                    {isExpanded && (
                      <div className="divide-y divide-gray-100">
                        {visibleChildren.map((child) => {
                          const childPerms = getPermsForResources(child.resources);
                          if (childPerms.length === 0 && searchQuery) return null;

                          const childKey = `${parent.name}-${child.name}`;
                          const childExpanded = expandedChildren[childKey] !== false; // default expanded
                          const childCount = getSelectionCount(childPerms);

                          return (
                            <div key={childKey} className="bg-white">
                              {/* Child header */}
                              <div
                                className="flex items-center justify-between px-6 py-2 cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleChild(childKey)}
                              >
                                <div className="flex items-center gap-2">
                                  <svg className={`w-3 h-3 transition-transform text-gray-400 ${childExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span className="text-sm font-medium text-gray-800">{child.name}</span>
                                  {childPerms.length > 0 && (
                                    <span className="text-xs text-gray-400">
                                      ({childCount.selected}/{childCount.total})
                                    </span>
                                  )}
                                </div>
                                {canManage && childPerms.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleAll(childPerms.map(p => p.id));
                                    }}
                                    className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                                  >
                                    {childCount.selected === childCount.total ? 'Deselect' : 'Select All'}
                                  </button>
                                )}
                              </div>

                              {/* Permissions grid */}
                              {childExpanded && childPerms.length > 0 && (
                                <div className="px-10 pb-2 flex flex-wrap gap-x-4 gap-y-1">
                                  {childPerms.map((perm) => (
                                    <label
                                      key={perm.id}
                                      className="flex items-center space-x-1.5 cursor-pointer min-w-[120px]"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedPermissions.includes(perm.id)}
                                        onChange={() => togglePermission(perm.id)}
                                        disabled={!canManage}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                                      />
                                      <span className="text-xs text-gray-700">{formatAction(perm.action)}</span>
                                    </label>
                                  ))}
                                </div>
                              )}

                              {childExpanded && childPerms.length === 0 && !searchQuery && (
                                <div className="px-10 pb-2 text-xs text-gray-400 italic">
                                  No permissions defined yet
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Other (unmatched) permissions */}
              {otherPermissions.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                    <span className="font-semibold text-gray-900">Other</span>
                    {canManage && (
                      <button
                        onClick={() => toggleAll(otherPermissions.map(p => p.id))}
                        className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        {otherPermissions.every(p => selectedPermissions.includes(p.id)) ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div className="px-6 py-2">
                    {/* Group by resource */}
                    {Object.entries(
                      otherPermissions.reduce((acc, p) => {
                        if (!acc[p.resource]) acc[p.resource] = [];
                        acc[p.resource].push(p);
                        return acc;
                      }, {})
                    ).map(([resource, perms]) => (
                      <div key={resource} className="mb-2">
                        <div className="text-sm font-medium text-gray-700 capitalize mb-1">{resource.replace(/_/g, ' ')}</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 pl-2">
                          {perms.map(perm => (
                            <label key={perm.id} className="flex items-center space-x-1.5 cursor-pointer min-w-[120px]">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                disabled={!canManage}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                              />
                              <span className="text-xs text-gray-700">{formatAction(perm.action)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 pt-4 border-t flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {canManage ? 'Cancel' : 'Close'}
              </button>
              {canManage && (
                <button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {saving ? 'Saving...' : 'Save Permissions'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
